<template>
  <div class="sidebar-shell">
    <div class="logo-area">
      <div class="logo-icon">
        <el-icon><Monitor /></el-icon>
      </div>
      <span class="logo-text">实验室管理系统</span>
    </div>

    <div class="user-card">
      <el-avatar :size="36" class="user-avatar">
        {{ currentUser?.name?.charAt(0) || '用户' }}
      </el-avatar>
      <div class="user-info">
        <span class="user-name">{{ currentUser?.name || '用户' }}</span>
        <el-tag size="small" :type="getRoleType(currentUser?.role)" effect="plain" class="user-role">
          {{ currentUser?.role || '未知' }}
        </el-tag>
      </div>
    </div>

    <el-menu
      :default-active="routePath"
      class="nav-menu"
      router
      :collapse="false"
    >
      <el-menu-item index="/">
        <el-icon><House /></el-icon>
        <template #title>首页</template>
      </el-menu-item>

      <el-menu-item index="/messages">
        <el-icon><ChatDotRound /></el-icon>
        <template #title>
          <span>消息中心</span>
          <el-badge v-if="messageCount > 0" :value="messageCount" :max="99" class="nav-badge" />
        </template>
      </el-menu-item>

      <el-menu-item index="/ai">
        <el-icon><MagicStick /></el-icon>
        <template #title>AI助手</template>
      </el-menu-item>

      <el-menu-item index="/tasks">
        <el-icon><Checked /></el-icon>
        <template #title>{{ ['管理员', '教师'].includes(currentUser?.role) ? '任务管理' : '我的任务' }}</template>
      </el-menu-item>

      <el-menu-item index="/approvals">
        <el-icon><Finished /></el-icon>
        <template #title>
          <span>{{ ['管理员', '教师'].includes(currentUser?.role) ? '审批管理' : '我的申请' }}</span>
          <el-badge v-if="approvalCount > 0" :value="approvalCount" :max="99" class="nav-badge" />
        </template>
      </el-menu-item>

      <el-menu-item index="/documents">
        <el-icon><Document /></el-icon>
        <template #title>文档中心</template>
      </el-menu-item>

      <el-menu-item index="/calendar">
        <el-icon><Calendar /></el-icon>
        <template #title>日历视图</template>
      </el-menu-item>

      <el-divider class="menu-divider" />

      <el-sub-menu index="equipment">
        <template #title>
          <el-icon><Tools /></el-icon>
          <span>设备管理</span>
        </template>
        <el-menu-item index="/equipments">
          <el-icon><Box /></el-icon>
          <template #title>设备列表</template>
        </el-menu-item>
        <el-menu-item index="/categories" v-if="currentUser?.role === '管理员'">
          <el-icon><Collection /></el-icon>
          <template #title>设备分类</template>
        </el-menu-item>
        <el-menu-item index="/borrows">
          <el-icon><Tickets /></el-icon>
          <template #title>借用管理</template>
        </el-menu-item>
      </el-sub-menu>

      <el-sub-menu index="admin" v-if="currentUser?.role === '管理员'">
        <template #title>
          <el-icon><Setting /></el-icon>
          <span>系统管理</span>
        </template>
        <el-menu-item index="/profiles">
          <el-icon><User /></el-icon>
          <template #title>用户管理</template>
        </el-menu-item>
        <el-menu-item index="/audit-logs">
          <el-icon><DocumentCopy /></el-icon>
          <template #title>审计日志</template>
        </el-menu-item>
      </el-sub-menu>
    </el-menu>

    <div class="aside-footer">
      <el-dropdown @command="emitCommand" trigger="click">
        <div class="footer-action">
          <el-icon><SwitchButton /></el-icon>
          <span>退出登录</span>
        </div>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="profile">
              <el-icon><User /></el-icon>
              个人中心
            </el-dropdown-item>
            <el-dropdown-item command="logout" divided>
              <el-icon><SwitchButton /></el-icon>
              退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup>
import {
  Monitor,
  House,
  ChatDotRound,
  Document,
  User,
  Calendar,
  Tools,
  Collection,
  Tickets,
  Checked,
  Finished,
  Box,
  Setting,
  DocumentCopy,
  SwitchButton,
  MagicStick
} from '@element-plus/icons-vue'

defineProps({
  currentUser: {
    type: Object,
    default: null
  },
  messageCount: {
    type: Number,
    default: 0
  },
  approvalCount: {
    type: Number,
    default: 0
  },
  routePath: {
    type: String,
    default: '/'
  }
})

const emit = defineEmits(['command'])

const emitCommand = (command) => {
  emit('command', command)
}

const getRoleType = (role) => {
  const map = {
    '管理员': 'danger',
    '教师': 'warning',
    '导师': 'warning',
    '硕士生': 'success',
    '博士生': 'primary'
  }
  return map[role] || 'info'
}
</script>

<style scoped>
.sidebar-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.logo-area {
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid #f0f0f0;
}

.logo-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #3370ff 0%, #0057ff 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  margin-right: 12px;
}

.logo-text {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
  white-space: nowrap;
}

.user-card {
  padding: 16px;
  margin: 12px;
  background: #f7f8fa;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  background: linear-gradient(135deg, #3370ff 0%, #0057ff 100%);
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2329;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  width: fit-content;
  font-size: 11px;
  padding: 0 6px;
  height: 18px;
  line-height: 18px;
}

.nav-menu {
  flex: 1;
  border: none;
  overflow-y: auto;
  padding: 0 8px;
}

.nav-menu::-webkit-scrollbar {
  width: 4px;
}

.nav-menu::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 2px;
}

.nav-menu:not(.el-menu--collapse) {
  width: 220px;
}

.nav-menu .el-menu-item,
.nav-menu .el-sub-menu__title {
  height: 44px;
  line-height: 44px;
  border-radius: 8px;
  margin-bottom: 2px;
  color: #646a73;
  font-size: 14px;
}

.nav-menu .el-menu-item:hover,
.nav-menu .el-sub-menu__title:hover {
  background: #f0f2f5;
  color: #1f2329;
}

.nav-menu .el-menu-item.is-active {
  background: #e6f0ff;
  color: #3370ff;
}

.nav-menu .el-menu-item.is-active .el-icon {
  color: #3370ff;
}

.nav-menu .el-icon {
  font-size: 18px;
  margin-right: 10px;
  color: inherit;
}

.nav-badge {
  margin-left: auto;
}

.nav-badge :deep(.el-badge__content) {
  background: #3370ff;
  border: none;
}

.menu-divider {
  margin: 8px 0;
  border-color: #f0f0f0;
}

.nav-menu .el-sub-menu .el-menu-item {
  height: 40px;
  line-height: 40px;
  padding-left: 48px !important;
  font-size: 13px;
}

.aside-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
}

.footer-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #646a73;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.footer-action:hover {
  background: #fff1f0;
  color: #f56c6c;
}

.footer-action .el-icon {
  font-size: 16px;
}
</style>
